import {
  MeshBuilder,
  Vector3,
  Plane,
  PBRMaterial,
  StandardMaterial,
  Color3,
  Mesh
} from '@babylonjs/core';
import { MirrorTexture } from '@babylonjs/core/Materials/Textures/mirrorTexture';

/**
 * Creates a planar mirror and positions it vertically on the wall,
 * facing mirrorLookToPosition horizontally.
 *
 * @param {BABYLON.Scene} scene - The Babylon.js scene.
 * @param {number[]|BABYLON.Vector3} mirrorPosition - The mirror position (array as [x, y, z] or a Vector3).
 * @param {number[]|BABYLON.Vector3} mirrorLookToPosition - A point for the mirror to "look at" (array as [x, y, z] or a Vector3).
 */
export function setupMirror(scene, mirrorPosition, mirrorLookToPosition) {
  // Convert parameters to Vector3 if provided as arrays
  const finalMirrorPosition = Array.isArray(mirrorPosition)
    ? new Vector3(mirrorPosition[0], mirrorPosition[1], mirrorPosition[2])
    : mirrorPosition;
  const finalMirrorLookToPosition = Array.isArray(mirrorLookToPosition)
    ? new Vector3(mirrorLookToPosition[0], mirrorLookToPosition[1], mirrorLookToPosition[2])
    : mirrorLookToPosition;

  // ---------------------------
  //      MIRROR CREATION
  // ---------------------------

  // 1) Create a circular mirror mesh with a 0.6m diameter
  //    and enough tessellation for a smooth edge.
  const mirror = MeshBuilder.CreateDisc(
    'Mirror',
    {
      radius: 0.5,              // 0.3m radius → 0.6m diameter
      tessellation: 64,         // Higher value for smoother disc
      sideOrientation: Mesh.DOUBLESIDE
    },
    scene
  );

  // 2) Position and orient the mirror
  mirror.position = finalMirrorPosition;
  mirror.lookAt(finalMirrorLookToPosition);

  // ---------------------------
  //      MIRROR MATERIAL
  // ---------------------------

  // Create a standard material for reflection
  const mirrorMaterial = new StandardMaterial('mirrorMat', scene);
  mirrorMaterial.backFaceCulling = false;       // Already redundant with DOUBLESIDE, but explicit
  mirrorMaterial.sideOrientation = Mesh.DOUBLESIDE;

  // Attach a mirror texture to capture reflections
  mirrorMaterial.reflectionTexture = new MirrorTexture('mirrorRTT', 1024, scene, true);

  // ---------------------------
  //      REFLECTION SETUP
  // ---------------------------

  // Compute the mirror's world matrix for the correct normal
  mirror.computeWorldMatrix(true);
  const mirrorWorldMatrix = mirror.getWorldMatrix();

  // Get the mirror's normal vector (the disc is flat, so one normal is enough)
  const mirrorVertexData = mirror.getVerticesData('normal');
  let mirrorNormal = new Vector3(
    mirrorVertexData[0],
    mirrorVertexData[1],
    mirrorVertexData[2]
  );

  // Transform the normal to match the mirror's orientation in world space
  mirrorNormal = Vector3.TransformNormal(mirrorNormal, mirrorWorldMatrix);

  // Create the reflection plane for the mirror
  const mirrorPlane = Plane.FromPositionAndNormal(mirror.position, mirrorNormal);

  // Apply the reflection plane to the mirror’s reflection texture
  mirrorMaterial.reflectionTexture.mirrorPlane = mirrorPlane;

  // Render all scene meshes in the mirror reflection, except the mirror itself
  mirrorMaterial.reflectionTexture.renderList = scene.meshes.filter(
    (m) => m.name !== 'Mirror'
  );

  // Adjust reflection intensity
  mirrorMaterial.reflectionTexture.level = 0.95;

  // Reduce mirror’s diffuse color so it appears more reflective and not washed-out
  mirrorMaterial.diffuseColor = new Color3(0, 0, 0);
  mirrorMaterial.specularColor = new Color3(1, 1, 1);

  // Assign the material to the mirror mesh
  mirror.material = mirrorMaterial;
}
